/**
 * Staff-gated error nudge reporter.
 *
 * Composes with the existing installErrorCapture (shared) handler rather than
 * replacing it. Specifically:
 *   - Wraps app.config.errorHandler to also call pushError/showNudge after the
 *     existing handler runs (which reports to Sentinel's /frontend-errors).
 *   - Listens to `cs:api-error` CustomEvents (emitted by shared axios) — the
 *     only error channel installErrorCapture does NOT handle.
 *   - Does NOT re-register window.error or window.unhandledrejection; those are
 *     already handled by installErrorCapture to avoid double-listeners.
 *
 * Gate: only fires for authenticated staff/admin users (role !== 'client').
 * Deduplication: same message+url signature is suppressed for DEDUP_MS.
 */
import type { App } from 'vue'
import { useAuthStore } from '@claimshield/shared'
import { useSupportStore } from '../stores/supportStore'
import type { ErrorEntry } from './pageContext'

const DEDUP_MS = 30_000
const lastSeen = new Map<string, number>()

function allowed(): boolean {
  try {
    const auth = useAuthStore()
    return Boolean(auth.isAuthenticated && auth.user && auth.user.role !== 'client')
  } catch {
    // Pinia not ready yet during early boot
    return false
  }
}

// `nudge=false` records the error to the context ring buffer (so a later manual
// report still carries it) WITHOUT popping the "something broke" prompt.
function record(entry: ErrorEntry, nudge: boolean): void {
  if (!allowed()) return
  const sig = `${entry.message}|${entry.url ?? ''}`
  const now = Date.now()
  const prev = lastSeen.get(sig)
  if (prev && now - prev < DEDUP_MS) return
  lastSeen.set(sig, now)
  try {
    const store = useSupportStore()
    store.pushError(entry)
    if (nudge) store.showNudge(entry)
  } catch {
    // Store not ready; silently skip
  }
}

export function installErrorReporter(app: App): void {
  // Compose with the prior Vue errorHandler (set by installErrorCapture).
  // We capture it here — at call time, installErrorCapture has already run.
  const priorHandler = app.config.errorHandler

  app.config.errorHandler = (err, instance, info) => {
    // Record for the nudge first, then let the existing handler do its work
    // (Sentinel /frontend-errors report + toast).
    const message = (err instanceof Error ? err.message : String(err)) || 'Unknown error'
    record({ message, at: new Date().toISOString() }, true)

    if (priorHandler) {
      priorHandler(err, instance, info)
    }
  }

  // cs:api-error — emitted by shared axios on EVERY non-canceled failure (4xx,
  // 5xx, timeout, network). installErrorCapture does NOT listen to this event,
  // so this is the only place that handles it.
  //
  // Only POP the nudge for genuine server-side breakage (5xx) — that's what
  // "something broke, report it" means. Expected client errors (401 auth, 403
  // perms, 404 not-found, 409/422 validation) and connectivity blips (status
  // null = network/timeout, already surfaced by the shared toast) are routine
  // and must NOT pop the prompt; we still log them to the context buffer so a
  // later manual report carries them.
  if (typeof window !== 'undefined') {
    window.addEventListener('cs:api-error', (e: Event) => {
      const d = (e as CustomEvent<{ status: number | null; url: string; method: string; message: string }>).detail
      if (d.status === 401) return
      const entry = { message: d.message, status: d.status ?? undefined, url: d.url, at: new Date().toISOString() }
      const isServerError = typeof d.status === 'number' && d.status >= 500
      record(entry, isServerError)
    })
  }
}
